import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { Bell } from 'lucide-react';
import ClientTradesGrid from '../../components/grids/ClientTradesGrid';
import { WorkflowState } from '../../types/workflow';
import PdfHighlightOverlay, { FieldHighlight } from '../../components/pdf/PdfHighlightOverlay';
import ResizeHandle from '../../components/common/ResizeHandle';
import { clientService } from '../../services/clientService';
import { clientService as apiClientService } from '../../services/api/clientService';
import { rulesEngineService } from '../../services/api';
import { MatchedRule } from '../../types/rulesEngine';
import AlertModal from '../../components/common/AlertModal';
import BatchSigningModal from '../../components/contracts/BatchSigningModal';
import ContractExceptionsModal from '../../components/contracts/ContractExceptionsModal';
import MassContractUploadModal from '../../components/contracts/MassContractUploadModal';
import { useAuth } from '../../components/auth/AuthContext';
import { logger } from '../../utils/logger';
import { useEventStreamSimple } from '../../hooks/useEventStreamSimple';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSystemDate } from '../../hooks/useSystemDate';
import './ContractComparison.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use jsdelivr CDN (more reliable than unpkg)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Cache for signed URLs (valid for 1 hour)
interface SignedUrlCache {
  signedUrl: string;
  expiresAt: number; // timestamp in milliseconds
}
const signedUrlCache = new Map<string, SignedUrlCache>();

const ContractComparison: React.FC = () => {
  const { t } = useTranslation();
  const [selectedTrade, setSelectedTrade] = useState<any>(null);
  const [contractSignedUrl, setContractSignedUrl] = useState<string | null>(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  const [localFileUrl, setLocalFileUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageHeight, setPageHeight] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.5); // Zoom level: 1.5 = 150%
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [analyzingContract, setAnalyzingContract] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null); // Stores LLM analysis results
  const [sendingForSignature, setSendingForSignature] = useState(false);
  const [signing, setSigning] = useState(false);
  const [gridRowUpdate, setGridRowUpdate] = useState<{ tradeId: string; fields: Record<string, any> } | null>(null);
  const { user } = useAuth();
  const [alertModal, setAlertModal] = useState<{ title: string; message: string; type: 'info' | 'error' | 'success' } | null>(null);
  const [showSignatureDetail, setShowSignatureDetail] = useState(false);
  const [showFixingDatesModal, setShowFixingDatesModal] = useState(false);
  const [showBatchSigningModal, setShowBatchSigningModal] = useState(false);
  const [showContractExceptions, setShowContractExceptions] = useState(false);
  const [showMassUploadModal, setShowMassUploadModal] = useState(false);
  const [contractExceptionsCount, setContractExceptionsCount] = useState(0);
  const [gridRefreshTrigger, setGridRefreshTrigger] = useState(0);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const [signingRule, setSigningRule] = useState<MatchedRule | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const clientId = user?.organization?.id || user?.id;

  // System date + date range filter (last 3 business days by default: T-2 → T0)
  const systemDateResult = useSystemDate(clientId);
  const { systemDate, previousBusinessDay2 } = systemDateResult;
  const [dateFrom, setDateFrom] = useState<string | null>(previousBusinessDay2 || null);
  const [dateTo, setDateTo] = useState<string | null>(systemDate || null);
  const dateDefaultsSet = useRef(false);

  useEffect(() => {
    if (systemDate && previousBusinessDay2 && !dateDefaultsSet.current) {
      dateDefaultsSet.current = true;
      setDateFrom(previousBusinessDay2);
      setDateTo(systemDate);
    }
  }, [systemDate, previousBusinessDay2]);

  // Load contract exceptions count (KAN-195)
  const loadContractExceptionsCount = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await apiClientService.getContractExceptions(clientId, 'open');
      if (result.success && result.data) {
        setContractExceptionsCount(result.data.counts?.total ?? 0);
      }
    } catch (err) {
      logger.error('Failed to load contract exceptions count:', err);
    }
  }, [clientId]);

  useEffect(() => {
    loadContractExceptionsCount();
  }, [loadContractExceptionsCount]);

  // Real-time signature updates via SSE
  useEventStreamSimple({
    clientId: clientId,
    eventTypes: ['signature_updated'],
    onEvent: (eventData) => {
      if (eventData.type !== 'signature_updated') return;

      // Ignore catch-up events older than 2 minutes
      const ageMs = Date.now() - new Date(eventData.timestamp).getTime();
      if (ageMs > 120_000) return;

      // Don't refresh if a signing action is in flight — avoid overwriting optimistic state
      if (sendingForSignature || signing) return;

      const eventTradeId = eventData.payload?.trade_id;
      if (!eventTradeId || !selectedTrade || eventTradeId !== selectedTrade.id) return;

      // Re-fetch the trade to pick up updated signature fields
      logger.info(`[ContractComparison] SSE signature_updated for trade ${eventTradeId}, refreshing...`);
      const tradeClientId = selectedTrade.organizationId || selectedTrade.ClientId;
      if (tradeClientId) {
        clientService.getForwardTrades(tradeClientId).then((trades: any[]) => {
          const updated = trades.find((t: any) => t.id === eventTradeId);
          if (updated) {
            // On COMPLETE: clear cached signed URLs so the PDF viewer fetches the signed document
            if (eventData.payload?.event === 'COMPLETE') {
              if (updated.ContractUrl) signedUrlCache.delete(updated.ContractUrl);
              if (updated.SignedDocumentUrl) signedUrlCache.delete(updated.SignedDocumentUrl);
              setLocalFileUrl(null);
            }
            setSelectedTrade(updated);
            setGridRowUpdate({ tradeId: eventTradeId, fields: updated });
          }
        }).catch((err: any) => {
          logger.warn(`[ContractComparison] Failed to refresh trade after SSE event: ${err}`);
        });
      }
    },
  });

  // Generate highlights based on selected field and analysis results
  const highlights: FieldHighlight[] = useMemo(() => {
    if (!selectedField || !selectedTrade || !analysisResults?.fields) return [];

    const fieldData = analysisResults.fields[selectedField];
    if (!fieldData?.coordinates) return []; // Field not found in contract or no coordinates

    const matchStatus: 'match' | 'mismatch' | 'pending' =
      fieldData.matches === true ? 'match' :
      fieldData.matches === false ? 'mismatch' :
      'pending';

    return [{
      fieldName: selectedField,
      page: fieldData.page,
      coordinates: {
        x: fieldData.coordinates.x,
        y: fieldData.coordinates.y,
        width: fieldData.coordinates.width,
        height: fieldData.coordinates.height
      },
      matchStatus
    }];
  }, [selectedField, selectedTrade, analysisResults]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    logger.info(`PDF loaded successfully with ${numPages} pages`);
  };

  const onPageLoadSuccess = (page: any) => {
    // Get the actual rendered dimensions (includes scale)
    const { width, height, originalWidth, originalHeight } = page;
    setPageWidth(width);
    setPageHeight(height);
    logger.info(`Page loaded with dimensions: ${width}x${height} (original: ${originalWidth}x${originalHeight}, scale: ${scale})`);
  };

  const handleHighlightClick = (fieldName: string) => {
    logger.info(`Clicked on highlight: ${fieldName}`);
    setSelectedField(fieldName);
  };

  const handleFieldRowClick = (fieldName: string) => {
    setSelectedField(fieldName);
    logger.info(`Selected field row: ${fieldName}`);

    // Scroll to the highlight on the PDF
    const fieldData = analysisResults?.fields?.[fieldName];
    const coords = fieldData?.coordinates;
    if (coords && pageRef.current) {
      // Change to the correct page if needed
      const targetPage = fieldData.page;
      if (targetPage !== pageNumber) {
        setPageNumber(targetPage);
      }

      // Scroll the PDF container to bring the highlight into view
      setTimeout(() => {
        if (pageRef.current) {
          const scaledY = coords.y * scale;
          const container = pageRef.current.parentElement;
          if (container) {
            // Scroll to position with some offset so highlight is visible near top
            container.scrollTop = scaledY - 100;
          }
        }
      }, targetPage !== pageNumber ? 300 : 0); // Delay if changing pages
    }
  };

  // Handle contract analysis
  const handleAnalyzeContract = async () => {
    if (!selectedTrade) return;

    setAnalyzingContract(true);
    try {
      const tradeId = selectedTrade.id;
      const clientId = selectedTrade.organizationId || selectedTrade.ClientId;

      if (!clientId) {
        throw new Error('Client ID not found for trade');
      }

      logger.info(`Analyzing contract for trade ${tradeId} (client ${clientId})`);

      // Get Firebase auth token
      const { auth } = await import('../../config/firebase');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const token = await user.getIdToken();

      // Call the clients router endpoint (Firestore-based)
      const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
      const response = await fetch(`${baseUrl}/api/v1/clients/${clientId}/trades/${tradeId}/analyze-contract`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        // Structured error with code from backend (e.g. pdf_unreadable)
        if (typeof detail === 'object' && detail?.code === 'pdf_unreadable') {
          throw new Error(t('contractComparison.pdfUnreadable'));
        }
        throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Failed to analyze contract');
      }

      const result = await response.json();
      // APIResponse wraps data: unwrap if needed
      const resultData = result.data || result;

      logger.info('Contract analysis completed:', resultData);

      // Store analysis results in state
      setAnalysisResults(resultData.analysis);

      // Log detailed results to console
      console.log('=== CONTRACT ANALYSIS RESULTS ===');
      console.log('Trade Number:', resultData.trade_number);
      console.log('Number of Pages:', resultData.analysis.num_pages);
      console.log('Total Text Length:', resultData.analysis.text.length, 'characters');

      if (resultData.analysis.fields) {
        console.log('Fields Detected:', resultData.analysis.fields);
        const fieldsDetected = Object.keys(resultData.analysis.fields).length;
        const fieldsWithCoordinates = Object.values(resultData.analysis.fields).filter((f: any) => f.coordinates).length;
        const matchingFields = Object.values(resultData.analysis.fields).filter((f: any) => f.matches === true).length;
        const mismatchingFields = Object.values(resultData.analysis.fields).filter((f: any) => f.matches === false).length;

        console.log(`Fields: ${fieldsDetected} total, ${fieldsWithCoordinates} with coordinates`);
        console.log(`Matches: ${matchingFields} ✓, Mismatches: ${mismatchingFields} ✗`);

        // Use the status calculated by the backend (single source of truth)
        const compStatus = resultData.contract_status || 'Needs Review';
        const updatedFields: Record<string, any> = { ContractComparisonStatus: compStatus };
        if (resultData.bankTradeNumber) {
          updatedFields.bankTradeNumber = resultData.bankTradeNumber;
        }
        setGridRowUpdate({ tradeId: selectedTrade.id, fields: updatedFields });
      } else {
        console.log('Pages Data:', resultData.analysis.pages);
        console.log('Full Text:', resultData.analysis.text);

        setAlertModal({
          title: t('contractComparison.analyzeContract'),
          message: `Trade: ${resultData.trade_number} — ${resultData.analysis.num_pages} pages, ${resultData.analysis.pages.reduce((sum: number, p: any) => sum + p.words.length, 0)} words extracted.`,
          type: 'success',
        });
      }

      console.log('=================================');

    } catch (error: any) {
      logger.error('Error analyzing contract:', error);
      setAlertModal({ title: 'Error', message: error.message, type: 'error' });
    } finally {
      setAnalyzingContract(false);
    }
  };

  // Handle sending contract for electronic signature
  const handleSendForSignature = async () => {
    if (!selectedTrade) return;

    const clientId = selectedTrade.organizationId || selectedTrade.ClientId;
    if (!clientId) return;

    setSendingForSignature(true);
    try {
      const result = await clientService.sendForSignature(clientId, selectedTrade.id, signingRule?.ruleId);
      logger.info('Contract sent for signature:', result);

      // Update the selected trade in local state to reflect the new status
      const updates: Record<string, any> = {
        SignatureStatus: 'PENDING',
        SignatureRequestId: result.signature_request_id,
      };
      if (result.signatories?.length) {
        updates.SignatureSignatories = result.signatories;
      }
      if (result.flow) {
        updates.SignatureFlow = result.flow;
      }
      if (result.approvers?.length) {
        updates.SignatureApprovers = result.approvers;
        updates.SignatureApproverMoment = result.approver_moment || 'BEFORE';
      }
      setSelectedTrade((prev: any) => ({ ...prev, ...updates }));

      // Also update the grid row in-place so context menu reflects the new status
      setGridRowUpdate({ tradeId: selectedTrade.id, fields: updates });
    } catch (error: any) {
      logger.error('Error sending for signature:', error);
      setAlertModal({ title: 'Error', message: error.message, type: 'error' });
    } finally {
      setSendingForSignature(false);
    }
  };

  // Show the "Sign" button only if the user has the sign_documents permission
  const isUserASigner = user?.profile?.canSignDocuments ?? false;

  // Digital signature feature flag — gates all signing UI
  const digitalSignatureEnabled = user?.profile?.organization?.digitalSignatureEnabled ?? false;

  // Handle API-direct signing (create + sign in one step)
  const handleSignDocument = async () => {
    if (!selectedTrade || !user) return;

    const clientId = selectedTrade.organizationId || selectedTrade.ClientId;
    if (!clientId) return;

    setSigning(true);
    try {
      // Request browser geolocation (non-blocking — proceed even if denied)
      let geolocation: string | undefined;
      let geolocationReference: string | undefined;

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            maximumAge: 60000,
          });
        });
        geolocation = `${position.coords.latitude}, ${position.coords.longitude}`;
        geolocationReference = geolocation;
      } catch (geoError) {
        logger.warn('Geolocation not available:', geoError);
      }

      const signatureText = user.name || user.email;

      await clientService.signDocument(
        clientId,
        selectedTrade.id,
        signatureText,
        geolocation,
        geolocationReference,
        signingRule?.ruleId,
      );

      // Update local state — the document is now PENDING (webhook will update to SIGNED)
      const updates = { SignatureStatus: 'PENDING' };
      setSelectedTrade((prev: any) => ({ ...prev, ...updates }));
      setGridRowUpdate({ tradeId: selectedTrade.id, fields: updates });

      setAlertModal({
        title: t('contractComparison.signSuccess'),
        message: t('contractComparison.signSuccessMessage'),
        type: 'success',
      });
    } catch (error: any) {
      logger.error('Error signing document:', error);
      setAlertModal({ title: 'Error', message: error.message, type: 'error' });
    } finally {
      setSigning(false);
    }
  };

  // Handle signing an already-submitted document (user's turn after approval/sequential flow)
  const handleSignExistingDocument = async () => {
    if (!selectedTrade || !user) return;

    const clientId = selectedTrade.organizationId || selectedTrade.ClientId;
    if (!clientId) return;

    setSigning(true);
    try {
      let geolocation: string | undefined;
      let geolocationReference: string | undefined;

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            maximumAge: 60000,
          });
        });
        geolocation = `${position.coords.latitude}, ${position.coords.longitude}`;
        geolocationReference = geolocation;
      } catch (geoError) {
        logger.warn('Geolocation not available:', geoError);
      }

      const signatureText = user.name || user.email;

      await clientService.signExistingDocument(
        clientId,
        selectedTrade.id,
        signatureText,
        geolocation,
        geolocationReference,
      );

      setAlertModal({
        title: t('contractComparison.signSuccess'),
        message: t('contractComparison.signSuccessMessage'),
        type: 'success',
      });
    } catch (error: any) {
      logger.error('Error signing existing document:', error);
      setAlertModal({ title: 'Error', message: error.message, type: 'error' });
    } finally {
      setSigning(false);
    }
  };

  // Determine which document URL to show in the PDF viewer
  const displayContractUrl = selectedTrade?.SignatureStatus === 'SIGNED' && selectedTrade?.SignedDocumentUrl
    ? selectedTrade.SignedDocumentUrl
    : selectedTrade?.ContractUrl;

  // Memoize file and options to prevent unnecessary Document reloads
  const pdfFile = useMemo(() => ({ url: contractSignedUrl || '' }), [contractSignedUrl]);
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
    standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  const handleTradeSelected = (trade: any) => {
    // Clear local file URL when selecting a different trade or when contract is deleted
    if (localFileUrl) {
      URL.revokeObjectURL(localFileUrl);
      setLocalFileUrl(null);
    }
    // Clear contract preview if trade has no contract
    const hasContract = trade?.ContractUrl;
    if (!hasContract) {
      setContractSignedUrl(null);
      setContractError(null);
    }
    // Load persisted comparison results if available, otherwise clear
    if (trade?.ContractDiscrepancies?.length > 0) {
      const fields: Record<string, any> = {};
      for (const d of trade.ContractDiscrepancies) {
        fields[d.field] = {
          contractValue: d.contractValue,
          tradeValue: d.tradeValue,
          matches: d.result === 'match' ? true : d.result === 'mismatch' ? false : null,
          found: d.result !== 'not_found',
          coordinates: d.coordinates || null,
          page: d.page ?? null,
        };
      }
      setAnalysisResults({ fields });
    } else {
      setAnalysisResults(null);
    }
    setSelectedField(null);
    setSigningRule(null);

    setSelectedTrade(trade);
  };

  // Handler for auto-preview after contract upload
  const handleContractUploaded = (trade: any, file: File) => {
    // Create local object URL for instant preview
    const objectUrl = URL.createObjectURL(file);
    setLocalFileUrl(objectUrl);
    setContractSignedUrl(objectUrl);
    // Don't call setSelectedTrade - the row is already selected from right-click
    // Calling it here causes grid to re-render and lose selection
    setContractError(null);
    logger.info('Auto-previewing uploaded contract from local file');
  };

  // Cleanup object URLs when component unmounts or trade changes
  useEffect(() => {
    return () => {
      if (localFileUrl) {
        URL.revokeObjectURL(localFileUrl);
      }
    };
  }, [localFileUrl]);

  // Fetch signed URL whenever selected trade changes (with caching)
  useEffect(() => {
    const fetchContractSignedUrl = async () => {
      // If we have a local file URL from upload, don't fetch from backend
      if (localFileUrl) {
        return;
      }

      // Clear previous contract state
      setContractSignedUrl(null);
      setContractError(null);

      if (!selectedTrade) {
        return;
      }

      // Use signed document URL if available, otherwise original contract
      const contractUrl = displayContractUrl;

      if (!contractUrl) {
        return;
      }

      // Validate contract URL format before proceeding
      if (!contractUrl.startsWith('https://storage.googleapis.com/')) {
        logger.error(`Invalid contract URL format for trade: ${contractUrl}`);
        setContractError('Invalid contract URL format. Please try refreshing the page.');
        return;
      }

      // Check cache first
      const cacheKey = contractUrl;
      const cached = signedUrlCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        logger.info(`Using cached signed URL for contract`);
        setContractSignedUrl(cached.signedUrl);
        return;
      }

      setLoadingContract(true);

      try {
        // Fetch signed URL via Firestore/clients endpoint
        const clientId = selectedTrade.organizationId || selectedTrade.ClientId;
        if (!clientId) {
          throw new Error('Client ID not found for trade');
        }
        const signedUrl = await clientService.getContractSignedUrl(clientId, contractUrl);

        // Cache the signed URL for 55 minutes (safe buffer before 1-hour expiry)
        signedUrlCache.set(cacheKey, {
          signedUrl,
          expiresAt: Date.now() + (55 * 60 * 1000)
        });

        setContractSignedUrl(signedUrl);
        logger.info(`Generated and cached new signed URL for contract`);
      } catch (error) {
        logger.error('Failed to fetch contract signed URL:', error);
        setContractError(error instanceof Error ? error.message : 'Failed to load contract');
      } finally {
        setLoadingContract(false);
      }
    };

    fetchContractSignedUrl();
  }, [selectedTrade, localFileUrl, displayContractUrl]);

  // Evaluate selected trade against contract_signing rules
  useEffect(() => {
    let cancelled = false;

    const evaluateSigningRules = async () => {
      setSigningRule(null);

      if (!selectedTrade?.ContractUrl) return;

      const clientId = selectedTrade.organizationId || selectedTrade.ClientId;
      if (!clientId) return;

      try {
        const entity: Record<string, unknown> = {
          ProductType: selectedTrade.ProductType,
          IsAverageRateForward: selectedTrade.IsAverageRateForward,
          IsForwardStarting: selectedTrade.IsForwardStarting,
          SettlementType: selectedTrade.SettlementType,
          Direction: selectedTrade.Direction,
          CounterpartyName: selectedTrade.CounterpartyName,
          Currency1: selectedTrade.Currency1,
          Currency2: selectedTrade.Currency2,
          NotionalAmount: selectedTrade.QuantityCurrency1,
          ContraAmount: selectedTrade.QuantityCurrency2,
          TradeDate: selectedTrade.TradeDate,
          ValueDate: selectedTrade.ValueDate,
          MaturityDate: selectedTrade.MaturityDate,
          ParentTradeNumber: selectedTrade.ParentTradeNumber,
          IsInternalTrade: selectedTrade.IsInternalTrade,
          OurParty: selectedTrade.OurParty,
          Grouping1: selectedTrade.Grouping1,
          Grouping2: selectedTrade.Grouping2,
        };

        const response = await rulesEngineService.evaluate(clientId, {
          context: 'contract_signing',
          entity,
        });

        if (cancelled) return; // Trade changed while API was in flight

        if (response.success && response.data?.matchedRules?.length > 0) {
          const matched = response.data.matchedRules[0];
          setSigningRule(matched);
          logger.info(`Signing rule matched: "${matched.ruleName}" (${matched.ruleId})`, matched.action);
        } else {
          logger.info('No contract_signing rules matched for this trade');
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('Error evaluating signing rules:', error);
        }
      }
    };

    evaluateSigningRules();

    return () => { cancelled = true; };
  }, [selectedTrade]);

  // Derive signing button visibility from the matched rule
  const signingAction = signingRule?.action as Record<string, any> | undefined;
  const isFxPlusPlatform = signingAction?.platform === 'fx_plus';
  const ruleSignatories: Array<{ email: string; fullName: string }> = signingAction?.signatories || [];
  const ruleApprovers: Array<{ email: string }> = signingAction?.approvers || [];
  const userEmailLower = user?.email?.toLowerCase() || '';
  const userEmailInSignatories = ruleSignatories.some(
    (s) => s.email.toLowerCase() === userEmailLower
  );
  const hasOtherSignatories = ruleSignatories.some(
    (s) => s.email.toLowerCase() !== userEmailLower
  );
  const hasBeforeApprovers = ruleApprovers.length > 0 && (signingAction?.approverMoment || 'before') === 'before';
  const signatoryFlow = (signingAction?.signatoryFlow || 'parallel').toLowerCase();
  const userSignatoryIndex = ruleSignatories.findIndex(
    (s) => s.email.toLowerCase() === userEmailLower
  );
  // "Firmar" (API-direct sign) is only valid when the user can sign immediately:
  // - No BEFORE approvers that need to act first
  // - User is the only signer, OR user is signer #1 in a sequential flow, OR flow is parallel
  const canSignDirectly = userEmailInSignatories
    && !hasBeforeApprovers
    && (ruleSignatories.length === 1 || signatoryFlow === 'parallel' || userSignatoryIndex === 0);
  // "Enviar para Firma" kicks off the SignAPIs-managed flow (approvers, sequential signing, notifications)
  const shouldShowSendForSignature = hasBeforeApprovers || hasOtherSignatories || (signatoryFlow === 'sequential' && ruleSignatories.length > 1);

  // "Firmar" on an already-submitted PENDING document (user's turn to sign)
  const canSignExisting = (() => {
    if (!selectedTrade || selectedTrade.SignatureStatus !== 'PENDING') return false;
    if (!isUserASigner || !isFxPlusPlatform) return false;

    // Check user is a PENDING signatory on the stored document
    const storedSignatories: Array<{ email: string; status: string }> = selectedTrade.SignatureSignatories || [];
    const userEntry = storedSignatories.find(
      (s) => s.email?.toLowerCase() === userEmailLower
    );
    if (!userEntry || userEntry.status !== 'PENDING') return false;

    // Check all BEFORE approvers have approved
    const storedApprovers: Array<{ status: string }> = selectedTrade.SignatureApprovers || [];
    const approverMoment = (selectedTrade.SignatureApproverMoment || '').toUpperCase();
    if (approverMoment === 'BEFORE' && storedApprovers.length > 0) {
      const allApproved = storedApprovers.every((a) => a.status === 'APPROVED');
      if (!allApproved) return false;
    }

    // Check sequential flow: all signatories before the user have signed
    const flow = (selectedTrade.SignatureFlow || 'PARALLEL').toUpperCase();
    if (flow === 'SEQUENTIAL') {
      const userIdx = storedSignatories.findIndex(
        (s) => s.email?.toLowerCase() === userEmailLower
      );
      for (let i = 0; i < userIdx; i++) {
        if (storedSignatories[i].status !== 'SIGNED') return false;
      }
    }

    return true;
  })();

  // Format date strings (DD-MM-YYYY or YYYY-MM-DD) to DD/MM/YYYY for display
  const formatDate = (d: string | undefined) => {
    if (!d) return '-';
    const s = String(d);
    // YYYY-MM-DD → DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, day] = s.split('-');
      return `${day}/${m}/${y}`;
    }
    // DD-MM-YYYY → DD/MM/YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
      const [day, m, y] = s.split('-');
      return `${day}/${m}/${y}`;
    }
    return s.replace(/-/g, '/');
  };

  const DATE_FIELDS = ['tradeDate', 'valueDate', 'maturityDate', 'paymentDate'];

  return (
    <div className="contract-comparison-page">
      {/* Loading overlay while analyzing or sending for signature */}
      {(analyzingContract || sendingForSignature || signing) && (
        <div className="analysis-overlay">
          <div className="analysis-spinner">
            <div className="spinner"></div>
            <p>{analyzingContract ? t('contractComparison.analyzing') : sendingForSignature ? t('contractComparison.sending') : t('contractComparison.signing')}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="contracts-header">
        <div className="contracts-title">{t('navigation.contracts')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {dateFrom !== null && (
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo || ''}
              onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
            />
          )}
          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
          <button
            className="exceptions-bell-btn"
            onClick={() => setShowContractExceptions(true)}
            title={t('contractExceptions.title', 'Contract Analysis Exceptions')}
          >
            <Bell size={20} />
            {contractExceptionsCount > 0 && (
              <span className="exceptions-badge">{contractExceptionsCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Resizable Panel Layout */}
      <PanelGroup
        orientation="vertical"
        className="contract-panel-group"
      >
        {/* Top Section - PDF and Comparison */}
        <Panel defaultSize={65} minSize={30} style={{ position: 'relative' }}>
          <PanelGroup
            orientation="horizontal"
            className="content-area"
          >
            {/* PDF Viewer - left side (resizable) */}
            <Panel defaultSize={75} minSize={30} style={{ position: 'relative' }}>
              <div className="pdf-viewer-section">
                <h3>{t('contractComparison.contractPreview')}</h3>
                {digitalSignatureEnabled && selectedTrade?.ContractUrl && (
                  <>
                    <div
                      className={`signature-overlay-badge ${
                        selectedTrade.SignatureStatus === 'SIGNED' ? 'signed' :
                        selectedTrade.SignatureStatus === 'PENDING' ? 'pending' :
                        selectedTrade.SignatureStatus === 'REJECTED' ? 'rejected' :
                        'not-requested'
                      }`}
                      onClick={selectedTrade.SignatureStatus ? () => setShowSignatureDetail(true) : undefined}
                    >
                      {selectedTrade.SignatureStatus === 'SIGNED' && t('contractComparison.documentSigned')}
                      {selectedTrade.SignatureStatus === 'PENDING' && t('contractComparison.signatureRequested')}
                      {selectedTrade.SignatureStatus === 'REJECTED' && t('contractComparison.signatureRejected')}
                      {!selectedTrade.SignatureStatus && t('contractComparison.signatureNotRequested')}
                    </div>
                    {showSignatureDetail && selectedTrade.SignatureStatus && (
                      <>
                        <div className="sig-detail-backdrop" onClick={() => setShowSignatureDetail(false)} />
                        <div className={`sig-detail-dropdown sig-detail-${selectedTrade.SignatureStatus.toLowerCase()}`}>
                          <div className="sig-detail-header">
                            <h3>{t('contractComparison.signatureDetails')}</h3>
                            <button className="sig-detail-close" onClick={() => setShowSignatureDetail(false)}>×</button>
                          </div>
                          <div className="sig-detail-content">
                            {selectedTrade.SignatureSentAt && (
                              <div className="sig-detail-sent">
                                {t('contractComparison.sentBy')} <strong>{selectedTrade.SignatureSentByName || selectedTrade.SignatureSentBy || '—'}</strong>{' '}
                                {t('contractComparison.sentOn')}{' '}
                                {(() => {
                                  const d = new Date(selectedTrade.SignatureSentAt?.seconds ? selectedTrade.SignatureSentAt.seconds * 1000 : selectedTrade.SignatureSentAt);
                                  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                                })()}
                              </div>
                            )}
                            {/* Render sections in flow order with arrows */}
                            {(() => {
                              const signatories = selectedTrade.SignatureSignatories || [];
                              const approvers = selectedTrade.SignatureApprovers || [];
                              const moment = (selectedTrade.SignatureApproverMoment || 'BEFORE').toUpperCase();
                              const sigFlow = (selectedTrade.SignatureFlow || 'PARALLEL').toUpperCase();
                              const isSequential = sigFlow === 'SEQUENTIAL';

                              const formatDate = (ts: any) => {
                                const d = new Date(ts?.seconds ? ts.seconds * 1000 : ts);
                                return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                              };

                              const isParallel = !isSequential && signatories.length > 1;

                              const flowArrow = () => (
                                <div className="sig-detail-flow-arrow">
                                  <div className="sig-detail-flow-shaft" />
                                  <div className="sig-detail-flow-head">&#x25BC;</div>
                                </div>
                              );

                              const mergeConnector = () => (
                                <div className="sig-detail-merge-arrow">
                                  <div className="sig-detail-merge-connector" />
                                  <div className="sig-detail-flow-shaft" />
                                  <div className="sig-detail-flow-head">&#x25BC;</div>
                                </div>
                              );

                              const renderRow = (person: any, statusKey: string, timestampField: string, timestampStatus: string, compact?: boolean) => (
                                <div className={`sig-detail-row${compact ? ' sig-detail-row-compact' : ''}`}>
                                  <div className="sig-detail-row-main">
                                    <div className="sig-detail-person">
                                      <span className="sig-detail-name">{person.fullName}</span>
                                      <span className="sig-detail-email">{person.email}</span>
                                    </div>
                                    <div className={`sig-detail-status ${(person.status || 'PENDING').toLowerCase()}`}>
                                      <span className="sig-detail-dot" />
                                      {t(`contractComparison.${statusKey}.${(person.status || 'PENDING').toLowerCase()}`)}
                                    </div>
                                  </div>
                                  {person.status === timestampStatus && person[timestampField] && (
                                    <div className="sig-detail-timestamp">{formatDate(person[timestampField])}</div>
                                  )}
                                  {person.status === 'REJECTED' && (
                                    <>
                                      {person.rejectedAt && <div className="sig-detail-timestamp">{formatDate(person.rejectedAt)}</div>}
                                      {person.observation && (
                                        <div className="sig-detail-observation">
                                          <span className="sig-detail-obs-label">{t('contractComparison.rejectionReason')}:</span> {person.observation}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              );

                              /* Build a flat list of items: labels, rows, and arrows — all siblings in one flex column */
                              const items: React.ReactNode[] = [];
                              const hasApprovers = approvers.length > 0;

                              const addApprovers = () => {
                                items.push(<div className="sig-detail-section-label" key="lbl-approvers">{t('contractComparison.approversSection')}</div>);
                                approvers.forEach((app: any, idx: number) => {
                                  items.push(<React.Fragment key={`app-${idx}`}>{renderRow(app, 'approverStatus', 'approvedAt', 'APPROVED')}</React.Fragment>);
                                });
                              };

                              const addSignatories = () => {
                                items.push(<div className="sig-detail-section-label" key="lbl-signatories">{t('contractComparison.signatoriesSection')}</div>);
                                if (signatories.length > 0) {
                                  if (isParallel) {
                                    items.push(
                                      <div className="sig-detail-parallel-row" key="parallel-sigs">
                                        {signatories.map((sig: any, idx: number) => (
                                          <React.Fragment key={idx}>{renderRow(sig, 'signatoryStatus', 'signedAt', 'SIGNED', true)}</React.Fragment>
                                        ))}
                                      </div>
                                    );
                                  } else {
                                    signatories.forEach((sig: any, idx: number) => {
                                      if (isSequential && idx > 0) items.push(<React.Fragment key={`sig-arrow-${idx}`}>{flowArrow()}</React.Fragment>);
                                      items.push(<React.Fragment key={`sig-${idx}`}>{renderRow(sig, 'signatoryStatus', 'signedAt', 'SIGNED')}</React.Fragment>);
                                    });
                                  }
                                } else {
                                  items.push(<div className="sig-detail-empty" key="no-sig">{t('contractComparison.noSignatories')}</div>);
                                }
                              };

                              if (moment === 'BEFORE') {
                                if (hasApprovers) {
                                  addApprovers();
                                  items.push(<React.Fragment key="section-arrow">{flowArrow()}</React.Fragment>);
                                }
                                addSignatories();
                              } else {
                                addSignatories();
                                if (hasApprovers) {
                                  items.push(<React.Fragment key="section-arrow">{isParallel ? mergeConnector() : flowArrow()}</React.Fragment>);
                                  addApprovers();
                                }
                              }

                              return <div className="sig-detail-flow-list">{items}</div>;
                            })()}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="pdf-viewer-content">
                  {loadingContract ? (
                    <div className="pdf-placeholder">
                      Loading contract...
                    </div>
                  ) : contractError ? (
                    <div className="pdf-placeholder error">
                      Error: {contractError}
                    </div>
                  ) : contractSignedUrl ? (
                    <div className="pdf-container">
                      <Document
                        file={pdfFile}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={<div className="pdf-placeholder">Loading PDF...</div>}
                        error={<div className="pdf-placeholder error">Failed to load PDF</div>}
                        options={pdfOptions}
                      >
                        <div ref={pageRef} style={{ position: 'relative' }}>
                          <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            loading={<div className="pdf-placeholder">Loading page...</div>}
                            onLoadSuccess={onPageLoadSuccess}
                          />
                          {pageWidth > 0 && pageHeight > 0 && (
                            <PdfHighlightOverlay
                              highlights={highlights}
                              currentPage={pageNumber}
                              pageWidth={pageWidth}
                              pageHeight={pageHeight}
                              scale={scale}
                              onClick={handleHighlightClick}
                            />
                          )}
                        </div>
                      </Document>
                      <div className="pdf-controls">
                        {numPages && numPages > 1 && (
                          <div className="pdf-navigation">
                            <button
                              onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                              disabled={pageNumber <= 1}
                              className="pdf-nav-btn"
                            >
                              Previous
                            </button>
                            <span className="pdf-page-info">
                              Page {pageNumber} of {numPages}
                            </span>
                            <button
                              onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                              disabled={pageNumber >= numPages}
                              className="pdf-nav-btn"
                            >
                              Next
                            </button>
                          </div>
                        )}
                        <div className="pdf-zoom-controls">
                          <button
                            onClick={() => setScale(prev => Math.max(0.5, prev - 0.25))}
                            disabled={scale <= 0.5}
                            className="pdf-nav-btn"
                          >
                            −
                          </button>
                          <span className="pdf-zoom-info">{Math.round(scale * 100)}%</span>
                          <button
                            onClick={() => setScale(prev => Math.min(3, prev + 0.25))}
                            disabled={scale >= 3}
                            className="pdf-nav-btn"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : selectedTrade ? (
                    <div className="pdf-placeholder">
                      {t('contractComparison.noContractUploaded')}
                    </div>
                  ) : (
                    <div className="pdf-placeholder">
                      {t('contractComparison.selectTradeMessage')}
                    </div>
                  )}
                </div>
              </div>
            </Panel>

            <ResizeHandle direction="horizontal" />

            {/* Comparison Panel - right side (resizable) */}
            <Panel defaultSize={25} minSize={15} style={{ position: 'relative' }}>
              <div className="comparison-panel">
                <div className="comparison-panel-header">
                  <h3>{t('contractComparison.tradeVsContract')}</h3>
                  <div className="comparison-panel-actions">
                    {contractSignedUrl && (
                      <button
                        onClick={handleAnalyzeContract}
                        disabled={analyzingContract}
                        className="analyze-contract-btn"
                      >
                        {analyzingContract ? t('contractComparison.analyzing') : t('contractComparison.analyzeContract')}
                      </button>
                    )}
                    {digitalSignatureEnabled && isUserASigner && isFxPlusPlatform && canSignDirectly && selectedTrade?.ContractUrl && !selectedTrade?.SignatureStatus && (
                      <button
                        onClick={handleSignDocument}
                        disabled={signing}
                        className="sign-document-btn"
                      >
                        {signing ? t('contractComparison.signing') : t('contractComparison.signDocument')}
                      </button>
                    )}
                    {digitalSignatureEnabled && isFxPlusPlatform && shouldShowSendForSignature && selectedTrade?.ContractUrl && !selectedTrade?.SignatureStatus && (
                      <button
                        onClick={handleSendForSignature}
                        disabled={sendingForSignature}
                        className="send-signature-btn"
                      >
                        {sendingForSignature ? t('contractComparison.sending') : t('contractComparison.sendForSignature')}
                      </button>
                    )}
                    {digitalSignatureEnabled && selectedTrade?.SignatureStatus === 'PENDING' && !canSignExisting && (
                      <span className="signature-status-inline pending">{t('contractComparison.signatureRequested')}</span>
                    )}
                    {digitalSignatureEnabled && canSignExisting && (
                      <button
                        onClick={handleSignExistingDocument}
                        disabled={signing}
                        className="sign-document-btn"
                      >
                        {signing ? t('contractComparison.signing') : t('contractComparison.signDocument')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="comparison-content">
                  {selectedTrade?.hasWorkflowExceptions && (
                    <div className="workflow-exception-notice" style={{
                      background: 'rgba(234, 179, 8, 0.1)',
                      border: '1px solid rgba(234, 179, 8, 0.3)',
                      borderRadius: '6px',
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                    }}>
                      <span style={{ color: '#eab308', fontWeight: 600, marginRight: '0.5rem' }}>*</span>
                      {(() => {
                        const history = (selectedTrade as any).workflowHistory as Array<any> | undefined;
                        const exceptionEntries = history?.filter((e: any) => e.type === 'exception_auto_transition') || [];
                        if (exceptionEntries.length > 0) {
                          const ruleNames = [...new Set(exceptionEntries.map((e: any) => e.ruleName).filter(Boolean))];
                          return t('trades.exceptions.noticeBannerWithRules',
                            'One or more workflow transitions were automatically executed on this trade via exception rule(s): {{rules}}.',
                            { rules: ruleNames.join(', ') }
                          );
                        }
                        return t('trades.exceptions.noticeBanner',
                          'One or more workflow transitions were automatically executed on this trade via a configured exception rule.'
                        );
                      })()}
                    </div>
                  )}
                  {selectedTrade ? (
                    <div className="comparison-table-container">
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>{t('contractComparison.tableHeaders.field')}</th>
                            <th>{t('contractComparison.tableHeaders.trade')}</th>
                            <th>{t('contractComparison.tableHeaders.contract')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { key: 'tradeNumber', label: t('grid.columns.tradeNumber'), value: selectedTrade.TradeNumber },
                            { key: 'ourParty', label: t('grid.columns.entity'), value: selectedTrade.OurParty || '-' },
                            { key: 'counterpartyName', label: t('grid.columns.counterparty'), value: selectedTrade.CounterpartyName || '-' },
                            { key: 'productType', label: t('grid.columns.productType'), value: selectedTrade.IsAverageRateForward ? `${selectedTrade.ProductType} (Asiático)` : selectedTrade.IsForwardStarting ? `${selectedTrade.ProductType} (Starting)` : selectedTrade.ProductType },
                            { key: 'tradeDate', label: t('grid.columns.tradeDate'), value: formatDate(selectedTrade.TradeDate) },
                            { key: 'valueDate', label: t('grid.columns.valueDate'), value: formatDate(selectedTrade.ValueDate) },
                            { key: 'direction', label: t('grid.columns.direction'), value: selectedTrade.Direction },
                            { key: 'currency1', label: t('grid.columns.currency1'), value: selectedTrade.Currency1 },
                            { key: 'quantityCurrency1', label: t('grid.columns.amount'), value: selectedTrade.QuantityCurrency1?.toLocaleString() || '-' },
                            { key: 'price', label: t('grid.columns.price'), value: selectedTrade.Price },
                            { key: 'currency2', label: t('grid.columns.currency2'), value: selectedTrade.Currency2 },
                            { key: 'maturityDate', label: t('grid.columns.maturityDate'), value: formatDate(selectedTrade.MaturityDate) },
                            { key: 'fixingReference', label: t('grid.columns.fixingReference'), value: selectedTrade.FixingReference || '-' },
                            { key: 'settlementType', label: t('grid.columns.settlementType'), value: selectedTrade.SettlementType || '-' },
                            { key: 'settlementCurrency', label: t('grid.columns.settlementCurrency'), value: selectedTrade.SettlementCurrency || '-' },
                            { key: 'paymentDate', label: t('grid.columns.paymentDate'), value: formatDate(selectedTrade.PaymentDate) },
                            // Asiático-only fields
                            ...(selectedTrade.IsAverageRateForward ? [
                              { key: 'averageRateFixingDates', label: t('grid.columns.fixingDates'), value: (() => {
                                const schedule = selectedTrade.AverageRateFixingSchedule;
                                if (!Array.isArray(schedule) || schedule.length === 0) return '-';
                                const dates = schedule.map((entry: any) => {
                                  const d = typeof entry === 'object' ? entry.Date : entry;
                                  return formatDate(String(d)) || String(d);
                                });
                                const first = dates[0];
                                const last = dates[dates.length - 1];
                                return `${dates.length} fechas (${first} – ${last})`;
                              })() },
                            ] : [])
                          ].map(field => {
                            const hasContract = !!contractSignedUrl;
                            const hasAnalysis = !!analysisResults?.fields;

                            // Get field data from analysis results
                            const fieldData = hasAnalysis ? analysisResults.fields[field.key] : null;
                            let contractValue = fieldData?.contractValue || '-';
                            // ProductType: if LLM detected this contract as Asiático, merge into display
                            if (field.key === 'productType' && hasAnalysis) {
                              const asiaticField = analysisResults.fields['isAverageRateForward'];
                              const contractIsAsiatico = asiaticField?.found &&
                                ['true', 'yes', 'sí', 'si'].includes(String(asiaticField.contractValue).toLowerCase());
                              if (contractIsAsiatico && contractValue === 'Forward') {
                                contractValue = 'Forward (Asiático)';
                              }
                            }
                            // Dates: normalize to DD/MM/YYYY for display
                            if (DATE_FIELDS.includes(field.key) && contractValue !== '-') {
                              contractValue = formatDate(contractValue) || contractValue;
                            }
                            // Numeric fields: format consistently with trade column
                            if (field.key === 'quantityCurrency1' && contractValue !== '-') {
                              const num = parseFloat(String(contractValue).replace(/,/g, ''));
                              if (!isNaN(num)) {
                                contractValue = num.toLocaleString();
                              }
                            }
                            if (field.key === 'price' && contractValue !== '-') {
                              const num = parseFloat(String(contractValue));
                              if (!isNaN(num)) {
                                contractValue = String(num);
                              }
                            }
                            const matches = fieldData?.matches;  // true, false, or null

                            // Fixing dates: summarize and show diff details when mismatched
                            if (field.key === 'averageRateFixingDates' && contractValue !== '-') {
                              const cDatesRaw = contractValue.split(',').map((s: string) => s.trim()).filter(Boolean);
                              const cDates = cDatesRaw.map((d: string) => formatDate(d) || d);
                              if (cDates.length > 2) {
                                let summary = `${cDates.length} fechas (${cDates[0]} – ${cDates[cDates.length - 1]})`;
                                // Show "rango expandido" indicator if dates were expanded from a range
                                if (fieldData?.expandedFromRange) {
                                  summary += ' [rango expandido]';
                                }
                                // If mismatch, show diff details using raw backend data
                                if (matches === false && fieldData?.tradeValue) {
                                  const tDates = String(fieldData.tradeValue).split(',').map((s: string) => s.trim()).filter(Boolean);
                                  if (tDates.length > 0) {
                                    // Normalize all to YYYYMMDD for set comparison
                                    const norm = (d: string) => {
                                      const clean = d.replace(/\//g, '-').trim();
                                      const parts = clean.split('-');
                                      if (parts.length === 3 && parts[0].length === 4) return `${parts[0]}${parts[1]}${parts[2]}`;
                                      if (parts.length === 3) return `${parts[2]}${parts[1]}${parts[0]}`;
                                      return clean;
                                    };
                                    const tradeSet = new Set(tDates.map(norm));
                                    const contractSet = new Set(cDates.map(norm));
                                    const onlyInContract = [...contractSet].filter(d => !tradeSet.has(d)).length;
                                    const onlyInTrade = [...tradeSet].filter(d => !contractSet.has(d)).length;
                                    const diffParts = [];
                                    if (cDates.length !== tDates.length) diffParts.push(`op: ${tDates.length} vs cont: ${cDates.length}`);
                                    if (onlyInContract > 0) diffParts.push(`+${onlyInContract} solo en contrato`);
                                    if (onlyInTrade > 0) diffParts.push(`+${onlyInTrade} solo en operación`);
                                    if (diffParts.length > 0) summary += ` [${diffParts.join(', ')}]`;
                                  }
                                }
                                contractValue = summary;
                              }
                            }
                            const isSelected = selectedField === field.key;
                            return (
                              <tr
                                key={field.key}
                                className={`comparison-row ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  if (field.key === 'averageRateFixingDates') {
                                    setShowFixingDatesModal(true);
                                  } else {
                                    hasContract && handleFieldRowClick(field.key);
                                  }
                                }}
                              >
                                <td className="field-name-cell">{field.label}</td>
                                <td className="trade-value-cell">{field.value}</td>
                                <td className="contract-value-cell">{hasAnalysis ? contractValue : '-'}</td>
                                <td className="status-cell">
                                  {hasAnalysis && matches === true && (
                                    <span className="status-icon match">✓</span>
                                  )}
                                  {hasAnalysis && matches === false && (
                                    <span className="status-icon mismatch">✗</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="info-text">{t('contractComparison.selectTradeMessage')}</p>
                  )}
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* Bottom Panel - Trades Grid */}
        <Panel defaultSize={35} minSize={15}>
          <div className="trades-panel expanded">
            <div className="panel-content">
              {dateFrom === null ? null : (
                <ClientTradesGrid
                  onRowSelected={handleTradeSelected}
                  hideUploadControls={true}
                  hideConfirmationColumns={true}
                  forwardTradesOnly={true}
                  menuContext="contracts"
                  onContractUploaded={handleContractUploaded}
                  workflowStates={['Confirmed', 'ContractSigned', 'Final'] as WorkflowState[]}
                  rowUpdate={gridRowUpdate}
                  refreshTrigger={gridRefreshTrigger}
                  dateFrom={dateFrom || undefined}
                  dateTo={dateTo || undefined}
                  clearFiltersTrigger={clearFiltersTrigger}
                />
              )}
            </div>
            {/* Action buttons — below the grid, outside panel-content so it's not clipped */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '4px 8px 0',
            }}>
              <button
                onClick={() => setClearFiltersTrigger(prev => prev + 1)}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                {t('common.clearFilters', 'Clear Filters')}
              </button>
              <button
                onClick={() => setShowMassUploadModal(true)}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                Carga Masiva
              </button>
              {digitalSignatureEnabled && (
                <button
                  onClick={() => setShowBatchSigningModal(true)}
                  style={{
                    padding: '0.3rem 0.75rem',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#218838'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#28a745'; }}
                >
                  Firma Batch
                </button>
              )}
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Alert Modal */}
      {alertModal && (
        <AlertModal
          isOpen={true}
          onClose={() => setAlertModal(null)}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
        />
      )}

      {/* Fixing Dates Comparison Modal */}
      {showFixingDatesModal && selectedTrade && (() => {
        // Build trade dates set (from AverageRateFixingSchedule → DD/MM/YYYY)
        const schedule = selectedTrade.AverageRateFixingSchedule;
        const tradeDates: string[] = Array.isArray(schedule)
          ? schedule.map((entry: any) => {
              const d = typeof entry === 'object' ? entry.Date : entry;
              return formatDate(String(d)) || String(d);
            })
          : [];

        // Build contract dates set (from analysis → DD/MM/YYYY)
        const rawContract = analysisResults?.fields?.averageRateFixingDates?.contractValue || '';
        const contractDates: string[] = rawContract
          .split(',')
          .map((s: string) => formatDate(s.trim()) || s.trim())
          .filter(Boolean);

        // Normalize to YYYYMMDD for comparison
        const toSortKey = (d: string) => {
          const parts = d.split('/');
          return parts.length === 3 ? `${parts[2]}${parts[1]}${parts[0]}` : d;
        };
        const tradeSet = new Set(tradeDates.map(toSortKey));
        const contractSet = new Set(contractDates.map(toSortKey));

        // Build unified sorted list of all dates
        const allKeys = new Set([...tradeSet, ...contractSet]);
        const sortedKeys = [...allKeys].sort();

        // Map YYYYMMDD back to DD/MM/YYYY
        const keyToDisplay = (k: string) => `${k.slice(6,8)}/${k.slice(4,6)}/${k.slice(0,4)}`;

        const onlyInTrade = sortedKeys.filter(k => tradeSet.has(k) && !contractSet.has(k)).length;
        const onlyInContract = sortedKeys.filter(k => !tradeSet.has(k) && contractSet.has(k)).length;

        return (
          <div className="alert-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowFixingDatesModal(false)}>
            <div className="fixing-dates-modal">
              <div className="alert-modal-header">
                <h3 className="alert-modal-title">Comparación de Fechas de Fixing</h3>
                <button className="alert-modal-close" onClick={() => setShowFixingDatesModal(false)}>×</button>
              </div>
              <div className="fixing-dates-summary">
                <span>Operación: <strong>{tradeDates.length}</strong> fechas</span>
                <span>Contrato: <strong>{contractDates.length}</strong> fechas</span>
                {onlyInTrade > 0 && <span className="fixing-diff-badge trade-only">+{onlyInTrade} solo en operación</span>}
                {onlyInContract > 0 && <span className="fixing-diff-badge contract-only">+{onlyInContract} solo en contrato</span>}
                {onlyInTrade === 0 && onlyInContract === 0 && tradeDates.length === contractDates.length && (
                  <span className="fixing-diff-badge match">Fechas idénticas</span>
                )}
              </div>
              <div className="fixing-dates-table-wrapper">
                <table className="fixing-dates-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Operación</th>
                      <th>Contrato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedKeys.map(key => {
                      const inTrade = tradeSet.has(key);
                      const inContract = contractSet.has(key);
                      const rowClass = inTrade && inContract ? '' : 'fixing-date-mismatch';
                      return (
                        <tr key={key} className={rowClass}>
                          <td>{keyToDisplay(key)}</td>
                          <td className={inTrade ? 'fixing-present' : 'fixing-absent'}>{inTrade ? '✓' : '—'}</td>
                          <td className={inContract ? 'fixing-present' : 'fixing-absent'}>{inContract ? '✓' : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="alert-modal-actions">
                <button className="alert-modal-button primary" onClick={() => setShowFixingDatesModal(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Batch Signing Modal */}
      {showBatchSigningModal && clientId && (
        <BatchSigningModal
          isOpen={showBatchSigningModal}
          clientId={clientId}
          userEmail={user?.email || ''}
          isDesignatedSignatory={isUserASigner}
          defaultDateFrom={dateFrom || undefined}
          defaultDateTo={dateTo || undefined}
          onClose={() => setShowBatchSigningModal(false)}
          onSuccess={(message) => {
            setShowBatchSigningModal(false);
            setSelectedTrade(null);
            setGridRefreshTrigger((prev) => prev + 1);
            setAlertModal({ title: 'Firma Batch', message, type: 'success' });
          }}
        />
      )}

      {/* Contract Exceptions Modal (KAN-195) */}
      {clientId && (
        <ContractExceptionsModal
          clientId={clientId}
          isOpen={showContractExceptions}
          onClose={() => {
            setShowContractExceptions(false);
            loadContractExceptionsCount();
          }}
          onRefreshNeeded={() => {
            setGridRefreshTrigger((prev) => prev + 1);
            loadContractExceptionsCount();
          }}
        />
      )}

      {/* Mass Contract Upload Modal (KAN-238) */}
      {showMassUploadModal && clientId && (
        <MassContractUploadModal
          isOpen={showMassUploadModal}
          clientId={clientId}
          onClose={() => setShowMassUploadModal(false)}
          onComplete={() => {
            setGridRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default ContractComparison;
